import React, { useState, useEffect, useRef } from "react";
import { FiTrash2, FiCopy, FiType } from "react-icons/fi";
import { userRequest } from '../../../utils/requestMethods';
import { toast } from 'react-toastify';
import WysiwygEditor from './WysiwygEditor';

const FormQuestion = ({ questionData, onUpdate, onDelete, onDuplicate, onToggleActive, isDeleting }) => {
  const [question, setQuestion] = useState(questionData.question || "");
  const [options, setOptions] = useState(questionData.options);
  const [questionType, setQuestionType] = useState(questionData.type || "multiple_choice");
  const [correctAnswer, setCorrectAnswer] = useState(questionData.correct_answer || "");
  const [point, setPoint] = useState(questionData.point !== undefined ? questionData.point : "");
  const [showCharPicker, setShowCharPicker] = useState(false);
  const [activeInputField, setActiveInputField] = useState(null); // 'question', 'option-X', 'answer'
  const optionRefs = useRef({});
  const answerRef = useRef(null);
  
  const handleQuestionChange = (html) => {
    setQuestion(html);
    onUpdate(questionData.id, { question: html });
  };
  
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(() => {
    // Find the index of the correct answer in options for multiple choice
    if (questionData.type === "multiple_choice" && questionData.correct_answer) {
      return questionData.options.findIndex(option => option === questionData.correct_answer);
    }
    return -1;
  });

  // Mathematical symbols organized by category
  const mathSymbols = {
    operators: [
      { symbol: '×', name: 'Multiplication' },
      { symbol: '÷', name: 'Division' },
      { symbol: '±', name: 'Plus-minus' },
      { symbol: '≠', name: 'Not equal' },
      { symbol: '≈', name: 'Approximately' },
      { symbol: '≤', name: 'Less than or equal' },
      { symbol: '≥', name: 'Greater than or equal' },
      { symbol: '∑', name: 'Summation' },
      { symbol: '∏', name: 'Product' },
      { symbol: '√', name: 'Square root' },
      { symbol: '∛', name: 'Cube root' },
      { symbol: '∞', name: 'Infinity' },
      { symbol: '∫', name: 'Integral' },
      { symbol: '∂', name: 'Partial derivative' },
      { symbol: '∇', name: 'Nabla' },
    ],
    greek: [
      { symbol: 'α', name: 'Alpha' },
      { symbol: 'β', name: 'Beta' },
      { symbol: 'γ', name: 'Gamma' },
      { symbol: 'δ', name: 'Delta' },
      { symbol: 'ε', name: 'Epsilon' },
      { symbol: 'θ', name: 'Theta' },
      { symbol: 'λ', name: 'Lambda' },
      { symbol: 'μ', name: 'Mu' },
      { symbol: 'π', name: 'Pi' },
      { symbol: 'ρ', name: 'Rho' },
      { symbol: 'σ', name: 'Sigma' },
      { symbol: 'τ', name: 'Tau' },
      { symbol: 'φ', name: 'Phi' },
      { symbol: 'ω', name: 'Omega' },
      { symbol: 'Δ', name: 'Delta (capital)' },
      { symbol: 'Σ', name: 'Sigma (capital)' },
      { symbol: 'Ω', name: 'Omega (capital)' },
    ],
    fractions: [
      { symbol: '½', name: 'One half' },
      { symbol: '⅓', name: 'One third' },
      { symbol: '⅔', name: 'Two thirds' },
      { symbol: '¼', name: 'One quarter' },
      { symbol: '¾', name: 'Three quarters' },
      { symbol: '⅕', name: 'One fifth' },
      { symbol: '⅖', name: 'Two fifths' },
      { symbol: '⅗', name: 'Three fifths' },
      { symbol: '⅘', name: 'Four fifths' },
      { symbol: '⅙', name: 'One sixth' },
      { symbol: '⅚', name: 'Five sixths' },
      { symbol: '⅛', name: 'One eighth' },
      { symbol: '⅜', name: 'Three eighths' },
      { symbol: '⅝', name: 'Five eighths' },
      { symbol: '⅞', name: 'Seven eighths' },
    ],
    sets: [
      { symbol: '∈', name: 'Element of' },
      { symbol: '∉', name: 'Not element of' },
      { symbol: '⊂', name: 'Subset of' },
      { symbol: '⊃', name: 'Superset of' },
      { symbol: '∪', name: 'Union' },
      { symbol: '∩', name: 'Intersection' },
      { symbol: '∅', name: 'Empty set' },
      { symbol: 'ℕ', name: 'Natural numbers' },
      { symbol: 'ℤ', name: 'Integers' },
      { symbol: 'ℚ', name: 'Rational numbers' },
      { symbol: 'ℝ', name: 'Real numbers' },
      { symbol: 'ℂ', name: 'Complex numbers' },
    ],
    angles: [
      { symbol: '∠', name: 'Angle' },
      { symbol: '°', name: 'Degree' },
      { symbol: '′', name: 'Minute' },
      { symbol: '″', name: 'Second' },
      { symbol: '⊥', name: 'Perpendicular' },
      { symbol: '∥', name: 'Parallel' },
    ],
    other: [
      { symbol: '°', name: 'Degree' },
      { symbol: '²', name: 'Superscript 2' },
      { symbol: '³', name: 'Superscript 3' },
      { symbol: '¹', name: 'Superscript 1' },
      { symbol: '₀', name: 'Subscript 0' },
      { symbol: '₁', name: 'Subscript 1' },
      { symbol: '₂', name: 'Subscript 2' },
      { symbol: '₃', name: 'Subscript 3' },
      { symbol: '₄', name: 'Subscript 4' },
      { symbol: '₅', name: 'Subscript 5' },
      { symbol: '₆', name: 'Subscript 6' },
      { symbol: '₇', name: 'Subscript 7' },
      { symbol: '₈', name: 'Subscript 8' },
      { symbol: '₉', name: 'Subscript 9' },
      { symbol: '‰', name: 'Per mille' },
      { symbol: '%', name: 'Percent' },
    ]
  };

  // Sync state with prop changes only when question ID changes (new/different question)
  useEffect(() => {
    // TinyMCE requires value to always be a string, not undefined or null
    setQuestion(questionData.question || "");
    setOptions(questionData.options || []);
    setQuestionType(questionData.type || "multiple_choice");
    setCorrectAnswer(questionData.correct_answer || "");
    setPoint(questionData.point !== undefined ? questionData.point : "");
    
    // Update selected option index based on correct answer
    if (questionData.type === "multiple_choice" && questionData.correct_answer) {
      const index = questionData.options.findIndex(option => option === questionData.correct_answer);
      setSelectedOptionIndex(index);
    } else {
      setSelectedOptionIndex(-1);
    }
  }, [questionData.id]); // Only sync when the question ID changes, not on every prop change

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...options];
    updatedOptions[index] = value;
    setOptions(updatedOptions);
    
    // Always update the correct answer if this option is currently selected
    if (selectedOptionIndex === index) {
      setCorrectAnswer(value);
      onUpdate(questionData.id, { options: updatedOptions, correct_answer: value });
    } else {
      onUpdate(questionData.id, { options: updatedOptions });
    }
  };

  const addOption = () => {
    const newOption = `Option ${options.length + 1}`;
    const updatedOptions = [...options, newOption];
    setOptions(updatedOptions);
    onUpdate(questionData.id, { options: updatedOptions });
  };

  const removeOption = (index) => {
    const updatedOptions = options.filter((_, i) => i !== index);
    setOptions(updatedOptions);
    onUpdate(questionData.id, { options: updatedOptions });
    
    // If the removed option was the correct answer, clear the selection
    if (selectedOptionIndex === index) {
      setSelectedOptionIndex(-1);
      setCorrectAnswer("");
      onUpdate(questionData.id, { correct_answer: "" });
    } else if (selectedOptionIndex > index) {
      // Adjust the selected index if an option before it was removed
      const newSelectedIndex = selectedOptionIndex - 1;
      setSelectedOptionIndex(newSelectedIndex);
      // Update the correct answer to match the new index
      if (newSelectedIndex >= 0 && newSelectedIndex < updatedOptions.length) {
        setCorrectAnswer(updatedOptions[newSelectedIndex]);
        onUpdate(questionData.id, { correct_answer: updatedOptions[newSelectedIndex] });
      }
    }
  };

  const handleQuestionTypeChange = (e) => {
    const newType = e.target.value;
    setQuestionType(newType);
    
    // If changing to text (short answer/descriptive) or true/false, clear options
    if (newType === "text" || newType === "true_or_false") {
      setOptions([]);
      setSelectedOptionIndex(-1);
      // Don't clear correct answer for text questions - teachers might want to keep their scoring guide
      if (newType === "true_or_false") {
        setCorrectAnswer("");
        onUpdate(questionData.id, { type: newType, options: [], correct_answer: "" });
      } else {
        onUpdate(questionData.id, { type: newType, options: [] });
      }
    } else {
      // For multiple choice, ensure we have at least one option
      if (options.length === 0) {
        const defaultOptions = ["Option 1"];
        setOptions(defaultOptions);
        setSelectedOptionIndex(-1);
        onUpdate(questionData.id, { type: newType, options: defaultOptions });
      } else {
        setSelectedOptionIndex(-1);
        onUpdate(questionData.id, { type: newType });
      }
    }
  };

  // Handler for selecting correct answer in multiple choice
  const handleOptionSelect = (index) => {
    setSelectedOptionIndex(index);
    setCorrectAnswer(options[index]);
    onUpdate(questionData.id, { correct_answer: options[index] });
  };

  // Handler for selecting true/false answer
  const handleTrueFalseSelect = (value) => {
    setCorrectAnswer(value);
    onUpdate(questionData.id, { correct_answer: value });
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  // Insert character at cursor position
  const insertCharacter = (char) => {
    if (!activeInputField) return;

    if (activeInputField === 'question') {
      // For WYSIWYG editor - insert at cursor position
      const editorElement = document.querySelector('.wysiwyg-editor-wrapper [contenteditable]');
      if (editorElement) {
        editorElement.focus();
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(char));
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          handleQuestionChange(editorElement.innerHTML || '');
        } else {
          editorElement.textContent += char;
          handleQuestionChange(editorElement.innerHTML || '');
        }
      }
    } else if (activeInputField.startsWith('option-')) {
      const index = parseInt(activeInputField.split('-')[1]);
      const currentValue = options[index] || '';
      const ref = optionRefs.current[index];
      
      if (ref && ref.selectionStart !== undefined) {
        // For regular text inputs
        const start = ref.selectionStart;
        const end = ref.selectionEnd;
        const newValue = currentValue.substring(0, start) + char + currentValue.substring(end);
        
        const updatedOptions = [...options];
        updatedOptions[index] = newValue;
        setOptions(updatedOptions);
        if (selectedOptionIndex === index) {
          setCorrectAnswer(newValue);
          onUpdate(questionData.id, { options: updatedOptions, correct_answer: newValue });
        } else {
          onUpdate(questionData.id, { options: updatedOptions });
        }
        
        // Set cursor position after inserted character
        setTimeout(() => {
          if (ref) {
            ref.focus();
            ref.setSelectionRange(start + char.length, start + char.length);
          }
        }, 0);
      } else {
        // Fallback: append to end
        const updatedOptions = [...options];
        updatedOptions[index] = currentValue + char;
        setOptions(updatedOptions);
        onUpdate(questionData.id, { options: updatedOptions });
      }
    } else if (activeInputField === 'answer') {
      const currentValue = correctAnswer || '';
      const ref = answerRef.current;
      
      if (ref && ref.selectionStart !== undefined) {
        // For regular text inputs
        const start = ref.selectionStart;
        const end = ref.selectionEnd;
        const newValue = currentValue.substring(0, start) + char + currentValue.substring(end);
        setCorrectAnswer(newValue);
        onUpdate(questionData.id, { correct_answer: newValue });
        
        // Set cursor position after inserted character
        setTimeout(() => {
          if (ref) {
            ref.focus();
            ref.setSelectionRange(start + char.length, start + char.length);
          }
        }, 0);
      } else {
        // Fallback: append to end
        const newValue = currentValue + char;
        setCorrectAnswer(newValue);
        onUpdate(questionData.id, { correct_answer: newValue });
      }
    }
  };

  // Handle input focus to track active field
  const handleInputFocus = (fieldType, index = null) => {
    const fieldId = index !== null ? `option-${index}` : fieldType;
    setActiveInputField(fieldId);
    // Don't close picker on focus - only close when clicking outside or close button
  };

  // Close character picker when clicking outside (but not when clicking symbols inside)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCharPicker) {
        const pickerContainer = event.target.closest('.char-picker-container');
        const mathSymbolsButton = event.target.closest('button[title="Insert math symbols"]');
        
        // Don't close if clicking inside the picker container (including symbols, category tabs, etc.)
        // Don't close if clicking the math symbols button that opened it
        // Only close if clicking outside the picker container
        if (!pickerContainer && !mathSymbolsButton) {
          setShowCharPicker(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCharPicker]);

  return (
    <div className="p-4 border rounded-2xl mt-4 shadow bg-white">
      {/* Question Input - Full Width */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-700">Question</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCharPicker(!showCharPicker);
                setActiveInputField('question');
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
              title="Insert math symbols"
            >
              <FiType className="w-3 h-3" />
              <span>Math Symbols</span>
            </button>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              questionType === "multiple_choice" 
                ? "bg-blue-100 text-blue-800" 
                : questionType === "true_or_false"
                ? "bg-green-100 text-green-800"
                : "bg-purple-100 text-purple-800"
            }`}>
              {questionType === "multiple_choice" 
                ? "Multiple Choice" 
                : questionType === "true_or_false"
                ? "True/False"
                : "Short Answer / Descriptive"}
            </span>
          </div>
        </div>
        <div className="relative">
          <div 
            onFocus={() => handleInputFocus('question')}
            className="question-editor-wrapper"
          >
            {questionData.id && (
              <WysiwygEditor
                value={question || ""}
                onChange={handleQuestionChange}
                placeholder={questionType === "text" 
                  ? "Enter your question here. Students will provide written answers..."
                  : "Untitled Question"}
                minHeight={100}
              />
            )}
          </div>
          {showCharPicker && activeInputField === 'question' && (
            <MathSymbolPicker
              symbols={mathSymbols}
              onInsert={insertCharacter}
              onClose={() => setShowCharPicker(false)}
            />
          )}
        </div>
      </div>

      {/* Question Type and Status - Side by Side */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Question Type */}
        <div>
          <span className="text-gray-700 block mb-2">Question Type</span>
          <select
            value={questionType}
            onChange={handleQuestionTypeChange}
            className="block w-full p-2.5 bg-gray-100 border-gray-200 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="multiple_choice">Multiple Choice</option>
            <option value="true_or_false">True or False</option>
            <option value="text">Short Answer / Descriptive</option>
          </select>
        </div>

        {/* Question Status */}
        <div>
          <span className="text-gray-700 font-medium block mb-2">Question Status</span>
          <div className="flex items-center space-x-2">
            <span className={`text-sm font-medium ${questionData.is_active ? 'text-emerald-600' : 'text-gray-500'}`}>
              {questionData.is_active ? 'Active' : 'Inactive'}
            </span>
            {onToggleActive && (
              <button
                onClick={() => onToggleActive(questionData.id)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  questionData.is_active ? 'bg-emerald-600' : 'bg-gray-300'
                } cursor-pointer`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    questionData.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Options - Only show for multiple choice */}
      {questionType === "multiple_choice" && (
        <div className="mt-3">
          <div className="mb-3">
            <span className="text-sm font-medium text-gray-700">Options</span>
            <p className="text-xs text-gray-500 mt-1">Click on an circle on the left of an option to mark it as the correct answer</p>
          </div>
          {options.map((option, index) => (
            <div 
              key={index} 
              className={`flex items-center space-x-2 mt-2 p-2 rounded-lg transition-colors cursor-pointer ${
                selectedOptionIndex === index 
                  ? 'bg-green-50 border-2 border-green-300' 
                  : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => handleOptionSelect(index)}
            >
              <div className="flex items-center space-x-2 flex-grow">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  selectedOptionIndex === index 
                    ? 'border-green-500 bg-green-500' 
                    : 'border-gray-300'
                }`}>
                  {selectedOptionIndex === index && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-grow flex items-center gap-1 relative">
                  <input
                    ref={(el) => {
                      if (el) optionRefs.current[index] = el;
                    }}
                    type="text"
                    className="flex-grow bg-transparent border-none text-sm focus:ring-0 focus:outline-none"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    onFocus={(e) => {
                      handleFocus(e);
                      handleInputFocus('option', index);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCharPicker(!showCharPicker);
                      setActiveInputField(`option-${index}`);
                      setTimeout(() => {
                        if (optionRefs.current[index]) {
                          optionRefs.current[index].focus();
                        }
                      }, 0);
                    }}
                    className="text-indigo-600 hover:text-indigo-700 p-1 flex-shrink-0"
                    title="Insert math symbols"
                  >
                    <FiType className="w-4 h-4" />
                  </button>
                  {showCharPicker && activeInputField === `option-${index}` && (
                    <div className="absolute top-full right-0 z-50 mt-1">
                      <MathSymbolPicker
                        symbols={mathSymbols}
                        onInsert={insertCharacter}
                        onClose={() => setShowCharPicker(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
              {options.length > 1 && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(index);
                  }} 
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
            </div>
          ))}

          <button onClick={addOption} className="text-blue-500 mt-2 hover:text-blue-700 text-sm">
            + Add option
          </button>
          
          {selectedOptionIndex !== -1 && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-green-800 font-medium">
                  Correct Answer: "{options[selectedOptionIndex]}"
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <hr className="my-3" />

      {/* Answer Input Section */}
      {questionType === "text" && (
        <div className="mt-4">
          {/* Scoring Guide Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Scoring Guide for Teachers</h4>
                <p className="text-sm text-blue-800 mb-2">
                  Provide a scoring rubric or expected answer to help evaluate student responses:
                </p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• <strong>Key points:</strong> List important concepts students should mention</li>
                  <li>• <strong>Sample answer:</strong> Provide an example of a complete response</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Answer Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scoring Guide / Expected Answer <span className="text-gray-500">(Optional)</span>
            </label>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span></span>
                <button
                  type="button"
                  onClick={() => {
                    setShowCharPicker(!showCharPicker);
                    setActiveInputField('answer');
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
                  title="Insert math symbols"
                >
                  <FiType className="w-3 h-3" />
                  <span>Math Symbols</span>
                </button>
              </div>
              <textarea
                ref={answerRef}
                value={correctAnswer}
                onChange={(e) => {
                  setCorrectAnswer(e.target.value);
                  onUpdate(questionData.id, { correct_answer: e.target.value });
                }}
                onFocus={() => handleInputFocus('answer')}
                placeholder="Example: Key points to look for: 1) Definition of photosynthesis 2) Role of chlorophyll 3) Process explanation. Sample answer: Photosynthesis is the process by which plants convert sunlight into energy using chlorophyll..."
                rows="4"
                className="block w-full text-sm p-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              {showCharPicker && activeInputField === 'answer' && (
                <MathSymbolPicker
                  symbols={mathSymbols}
                  onInsert={insertCharacter}
                  onClose={() => setShowCharPicker(false)}
                />
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              💡 This helps our system score consistently. Students won't see this information.
            </p>
          </div>
        </div>
      )}

      {questionType === "true_or_false" && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Correct Answer
          </label>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => handleTrueFalseSelect("true")}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                correctAnswer === "true"
                  ? "bg-green-50 border-green-500 text-green-700"
                  : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                correctAnswer === "true" 
                  ? "border-green-500 bg-green-500" 
                  : "border-gray-300"
              }`}>
                {correctAnswer === "true" && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              <span className="font-medium">True</span>
            </button>
            
            <button
              type="button"
              onClick={() => handleTrueFalseSelect("false")}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                correctAnswer === "false"
                  ? "bg-green-50 border-green-500 text-green-700"
                  : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                correctAnswer === "false" 
                  ? "border-green-500 bg-green-500" 
                  : "border-gray-300"
              }`}>
                {correctAnswer === "false" && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              <span className="font-medium">False</span>
            </button>
          </div>
          
          {correctAnswer && (
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-green-800 font-medium">
                  Correct Answer: {correctAnswer.charAt(0).toUpperCase() + correctAnswer.slice(1)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Points Assignment */}
      <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Points/Marks <span className="text-gray-500 text-xs">(Optional - leave empty to share remaining marks)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            step="0.5"
            value={point}
            onChange={(e) => {
              const value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
              setPoint(value);
              onUpdate(questionData.id, { point: value === "" ? undefined : value });
            }}
            placeholder="Auto"
            className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          />
          <span className="text-sm text-gray-600">marks</span>
          {point !== "" && point !== undefined && (
            <span className="text-xs text-indigo-600 font-medium ml-auto">
              ✓ {point} mark{point !== 1 ? 's' : ''} assigned
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mt-4">
        {onDuplicate && (
          <button
            onClick={() => onDuplicate(questionData.id)}
            className="flex items-center space-x-2 text-blue-500 bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition duration-200"
          >
            <FiCopy size={18} />
            <span>Duplicate</span>
          </button>
        )}
        <button
          onClick={() => onDelete(questionData.id)}
          disabled={isDeleting}
          className="flex items-center space-x-2 text-red-500 bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition duration-200 disabled:opacity-50"
        >
          <FiTrash2 size={18} />
          <span>{isDeleting ? "Deleting..." : "Delete Question"}</span>
        </button>
      </div>
    </div>
  );
};

// Math Symbol Picker Component
const MathSymbolPicker = ({ symbols, onInsert, onClose }) => {
  const [activeCategory, setActiveCategory] = React.useState('operators');

  const categories = [
    { id: 'operators', label: 'Operators' },
    { id: 'greek', label: 'Greek Letters' },
    { id: 'fractions', label: 'Fractions' },
    { id: 'sets', label: 'Sets' },
    { id: 'angles', label: 'Angles' },
    { id: 'other', label: 'Other' },
  ];

  return (
    <div className="char-picker-container bg-white border-2 border-indigo-200 rounded-lg shadow-xl p-4 w-full max-w-2xl">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">Mathematical Symbols</h4>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
        >
          ×
        </button>
      </div>
      
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-200 pb-2">
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveCategory(cat.id);
            }}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              activeCategory === cat.id
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Symbols Grid */}
      <div className="max-h-48 overflow-y-auto">
        <div className="grid grid-cols-8 gap-2">
          {symbols[activeCategory]?.map((item, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onInsert(item.symbol);
              }}
              className="p-2 text-lg border border-gray-200 rounded-md hover:bg-indigo-50 hover:border-indigo-300 transition-colors cursor-pointer"
              title={item.name}
            >
              {item.symbol}
            </button>
          ))}
        </div>
      </div>
      
      <p className="text-xs text-gray-500 mt-3 text-center">
        Click a symbol to insert it at the cursor position
      </p>
    </div>
  );
};

export default FormQuestion;